import Header from '@/components/header/Header';
import GoBackArrow from '@/components/GoBackArrow/GoBackArrow';
import React, { useEffect, useState } from 'react';
import useIsMobileView from '@/hooks/useIsMobileView';
import InputSearch from '@/components/InputSearch/InputSearch';
// import FiltersButton from '@/components/FiltersButton/FiltersButton';
// import { Popover } from '@headlessui/react';
// import FiltersMenu from '@/pages/agentDashboard/components/FiltersMenu/FiltersMenu';
import AgentTable from '@/pages/agentDashboard/components/AgentTable/AgentTable';
import useGetAgentRakeShare from '@/services/rake/queries/useGetAgentRakeShare';
import { AgentRakeShare, AgentRakeSortOptions } from '@/services/rake/rake.types';
import SendModalContainer from '@/features/user-profile/components/SendModalContainer';
import MultipleTablesModal from '@/components/MultipleTablesModal/MultipleTablesModal';
import { TablesSeatsPreview } from '@/services/friends/friendsServicesTypes';
import { adaptTableStateToPreview } from '@/pages/agentDashboard/utils/utils';
import { SortingState } from '@tanstack/react-table';
import AgentStatsSection from '@/pages/agentDashboard/components/AgentStatsSection/AgentStatsSection';
import AgentCardsSection from '@/pages/agentDashboard/components/AgentCardsSection/AgentCardsSection';

const AgentDashboard = () => {
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const [referTransferId, setReferTransferId] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedTablesToShow, setSelectedTablesToShow] = useState<TablesSeatsPreview[]>([]);
  const [showTablesReferId, setShowTablesReferId] = useState('');
  const [showRefTableModal, setShowRefTableModal] = useState(false);

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'alias',
      desc: false,
    },
  ]);

  const isMobileView = useIsMobileView();
  const [filter, setFilter] = useState<string | undefined>();
  const {
    data: agentRakeShareData,
    isFetching,
    isRefetching,
    fetchNextPage,
  } = useGetAgentRakeShare({
    filter: filter ? { alias: filter } : undefined,
    sort: {
      orderBy: (['firstName', 'alias', 'createdAt', 'totalRake'].includes(sorting[0]?.id as string)
        ? sorting[0]?.id
        : 'alias') as AgentRakeSortOptions['orderBy'],
      sortOrder: sorting[0]?.desc ? 'desc' : 'asc',
    },
    paginationOptions: { skip: 0, take: 20 },
  });

  const flatData = React.useMemo(
    () => agentRakeShareData?.pages?.flatMap((page) => page.data) ?? [],
    [agentRakeShareData],
  ) as unknown[];

  const totalDBRowCount = agentRakeShareData?.pages[0].data.total ?? 0;
  const totalFetched = flatData.length;

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;

        if (scrollHeight - scrollTop - clientHeight < 500 && !isFetching && totalFetched < totalDBRowCount) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount],
  );

  React.useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  const onClickShowTablesHandler = (refId: string) => {
    setShowTablesReferId(refId);
    setShowRefTableModal(true);
  };

  const onCloseRefTableModal = () => {
    setShowRefTableModal(false);
    setShowTablesReferId('');
  };

  useEffect(() => {
    if (showTablesReferId) {
      const data = flatData as AgentRakeShare[];
      const selectedRefer = data?.find((refer: AgentRakeShare) => refer.user?.id === showTablesReferId);
      const adaptedTableData = adaptTableStateToPreview(selectedRefer?.tables || []);
      setSelectedTablesToShow(adaptedTableData);
    }
  }, [showTablesReferId, flatData]);

  const onInputSearchHandler = (value: string) => {
    setFilter(value);
  };

  return (
    <>
      <Header />
      <div className="flex h-[100vh] w-full flex-col gap-5 px-4 pt-2 md:px-10 md:pt-5">
        <section className="flex flex-col md:flex-row">
          <div className="flex w-full flex-col gap-1">
            <div className="flex w-full flex-wrap items-center gap-4">
              {!isMobileView && <GoBackArrow />}
              <span className="text-2xl font-bold text-white">Agent Dashboard</span>
            </div>
            <span className="text-sm font-semibold text-ph-white-50">
              You can track the progress of your referrals, rakeback and other metrics on this page
            </span>
          </div>
          <div className="flex w-full items-center justify-between gap-4 py-5 align-middle md:w-[42em]">
            <InputSearch fullWidth placeholder="Search..." handleNameSearch={onInputSearchHandler} />
            {/*TODO: this will be a future feature*/}
            {/*<Popover id="agentFilters" className="relative">*/}
            {/*  <FiltersButton />*/}
            {/*  <FiltersMenu />*/}
            {/*</Popover>*/}
          </div>
        </section>
        <AgentStatsSection />
        <AgentCardsSection />
        <section>
          <div
            className="scrollbar-phenom relative max-h-[84vh] w-full overflow-auto"
            ref={tableContainerRef}
            onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
          >
            <AgentTable
              setSorting={setSorting}
              data={flatData as AgentRakeShare[]}
              isFetching={isFetching}
              isRefetching={isRefetching}
              onClickShowTables={onClickShowTablesHandler}
              onClickTransfer={(referral) => {
                setReferTransferId(referral);
                setShowTransferModal(true);
              }}
              sorting={sorting}
            />
          </div>
        </section>
        {showTransferModal && (
          <SendModalContainer
            defaultTransferId={referTransferId}
            defaultTabIndex={2}
            open={showTransferModal}
            setClose={() => setShowTransferModal(false)}
          />
        )}
        {showRefTableModal && (
          <MultipleTablesModal
            isFetching={isFetching}
            tables={selectedTablesToShow}
            isOpen={showRefTableModal}
            onClose={onCloseRefTableModal}
            title="Referral's Tables"
          />
        )}
      </div>
    </>
  );
};

export default AgentDashboard;
